<template>
  <AuthLayout
    sidebar-title="Verify it is you"
    sidebar-text="Choose how you'd like to receive your verification code."
    :support-phone="supportPhone"
    :is-admin="isAdmin"
    title="How should we send your verification code?"
    subtitle="Both options use secure, encrypted connections"
    :card="false"
  >
    <div
      role="radiogroup"
      aria-label="How should we send your verification code?"
      @keydown="onGroupKeydown"
    >
      <button
        id="choose-method-sms"
        type="button"
        role="radio"
        class="auth-login__option"
        :class="{ 'auth-login__option--active': selected === MFA_METHOD.SMS }"
        :aria-checked="selected === MFA_METHOD.SMS ? 'true' : 'false'"
        :tabindex="selected === MFA_METHOD.SMS || !selected ? 0 : -1"
        @click="selectMethod(MFA_METHOD.SMS)"
      >
        <span class="auth-login__option-title">
          <AuthIcon name="smartphone" class="auth-login__option-icon" />
          Text Message (SMS)
        </span>
        <p class="auth-login__option-desc">We'll send a 6-digit code to your mobile phone.</p>
      </button>

      <button
        id="choose-method-email"
        type="button"
        role="radio"
        class="auth-login__option"
        :class="{ 'auth-login__option--active': selected === MFA_METHOD.EMAIL }"
        :aria-checked="selected === MFA_METHOD.EMAIL ? 'true' : 'false'"
        :tabindex="selected === MFA_METHOD.EMAIL ? 0 : -1"
        @click="selectMethod(MFA_METHOD.EMAIL)"
      >
        <span class="auth-login__option-title">
          <AuthIcon name="mail" class="auth-login__option-icon" />
          Email
        </span>
        <p class="auth-login__option-desc">We'll send a 6-digit code to your email.</p>
      </button>
    </div>

    <div v-if="!hideTrustOption" class="auth-login__row-between" style="margin-top: 20px">
      <AuthCheckbox id="remember-device-method" v-model="trustDevice" label="Remember for 90 days" />
    </div>

    <span v-if="error" class="auth-login__error-block" role="alert">{{ error }}</span>

    <div class="auth-login__actions">
      <AuthButton :loading="loading" :disabled="!selected" @click="handleSubmit">
        Send code
      </AuthButton>
      <AuthButton v-if="!isAdmin && showMagicLink" variant="ghost" :loading="loading" @click="emit('magic-link')">
        Or send me a magic link instead
      </AuthButton>
    </div>

    <button type="button" class="auth-login__back" :disabled="loading" @click="emit('cancel')">
      ← Back
    </button>
  </AuthLayout>
</template>

<script setup>
import { ref } from 'vue'
import { MFA_METHOD } from '../constants/steps.js'
import { isOptionNavigationKey, moveOptionSelection } from '../utils/optionRadiogroup.js'
import AuthLayout from '../../shared/components/AuthLayout.vue'
import AuthIcon from '../../shared/components/AuthIcon.vue'
import AuthButton from '../../shared/components/AuthButton.vue'
import AuthCheckbox from '../../shared/components/AuthCheckbox.vue'

defineProps({
  loading:         { type: Boolean, default: false },
  error:           { type: String,  default: '' },
  hideTrustOption: { type: Boolean, default: false },
  supportPhone:    { type: String,  default: '815-908-2020' },
  isAdmin:         { type: Boolean, default: false },
  showMagicLink:   { type: Boolean, default: false },
})

const emit = defineEmits(['submit', 'magic-link', 'cancel'])

const methodOptions = [MFA_METHOD.SMS, MFA_METHOD.EMAIL]
const selected = ref(null)
const trustDevice = ref(false)

const selectMethod = (method) => {
  selected.value = method
}

const onGroupKeydown = (event) => {
  if (!isOptionNavigationKey(event.key)) return

  event.preventDefault()
  selected.value = moveOptionSelection(methodOptions, selected.value, event.key)
}

const handleSubmit = () => {
  if (!selected.value) return
  emit('submit', { method: selected.value, trust: trustDevice.value })
}
</script>
